import { supabase, isSupabaseConfigured } from '../lib/supabase.js';
import { getCached, setCache } from '../lib/cache.js';
import { createCard } from './card.js';
import { loadAnimationWithKeyframes, rpcToLegacyProject } from '../lib/storage.js';

const PAGE_SIZE = 20;
let currentOffset = 0;
let hasMore = true;
let loading = false;

// In-memory cache of animation project data for hover previews
const animDataCache = new Map();

export function initExplore() {
  const page = document.getElementById('page-explore');
  if (!page) return;

  // Check if we already rendered the explore grid
  if (page.querySelector('.explore-grid')) return;

  // Replace placeholder with explore UI
  page.innerHTML = `
    <div class="explore-content">
      <div class="explore-header">
        <h1>Explore Animations</h1>
        <p>Discover Unicode character animations created by the community</p>
      </div>
      <div class="explore-grid" id="explore-grid"></div>
      <div class="explore-load-more" id="explore-load-more">
        <button class="explore-load-btn" id="btn-load-more">Load More</button>
      </div>
      <div class="explore-empty" id="explore-empty" style="display:none;">
        <div class="explore-mascot" id="explore-empty-mascot"></div>
        <p>No animations yet. Be the first!</p>
        <a href="#/editor" class="explore-cta">Open Editor</a>
      </div>
    </div>
  `;

  currentOffset = 0;
  hasMore = true;

  // Load initial data
  loadExploreAnimations();

  document.getElementById('btn-load-more').addEventListener('click', () => {
    loadExploreAnimations();
  });
}

async function loadExploreAnimations() {
  if (loading || !hasMore) return;
  loading = true;

  const grid = document.getElementById('explore-grid');
  const loadMoreEl = document.getElementById('explore-load-more');
  const emptyEl = document.getElementById('explore-empty');

  // Try cache first (only for first page)
  if (currentOffset === 0) {
    const cached = getCached('explore_page_0');
    if (cached) {
      renderCards(grid, cached);
      currentOffset = cached.length;
      hasMore = cached.length >= PAGE_SIZE;
      // Still fetch fresh data in background
      fetchAndUpdate(grid, 0, cached);
      loading = false;
      return;
    }
  }

  if (!isSupabaseConfigured()) {
    // Show empty state with animated mascot
    if (currentOffset === 0) {
      loadMoreEl.style.display = 'none';
      emptyEl.style.display = 'flex';
      startEmptyMascotAnimation();
    }
    loading = false;
    return;
  }

  try {
    const { data, error } = await supabase.rpc('get_explore_animations', {
      p_limit: PAGE_SIZE,
      p_offset: currentOffset,
    });

    if (error) throw error;

    if (currentOffset === 0) {
      setCache('explore_page_0', data);
    }

    if (!data || data.length === 0) {
      hasMore = false;
      if (currentOffset === 0) {
        loadMoreEl.style.display = 'none';
        emptyEl.style.display = 'flex';
        startEmptyMascotAnimation();
      } else {
        loadMoreEl.style.display = 'none';
      }
    } else {
      renderCards(grid, data);
      currentOffset += data.length;
      hasMore = data.length >= PAGE_SIZE;
      if (!hasMore) loadMoreEl.style.display = 'none';
      // Pre-fetch animation data for hover previews
      prefetchAnimationData(data.map(a => a.id));
    }
  } catch (e) {
    console.warn('Failed to load explore animations:', e);
    if (currentOffset === 0) {
      loadMoreEl.style.display = 'none';
      emptyEl.style.display = 'flex';
      startEmptyMascotAnimation();
    }
  }

  loading = false;
}

function renderCards(grid, animations) {
  for (const anim of animations) {
    const card = createCard(anim, onHoverPlay);
    grid.appendChild(card);
  }
}

async function fetchAndUpdate(grid, offset, cachedData) {
  if (!isSupabaseConfigured()) return;

  try {
    const { data } = await supabase.rpc('get_explore_animations', {
      p_limit: PAGE_SIZE,
      p_offset: offset,
    });

    if (data && JSON.stringify(data) !== JSON.stringify(cachedData)) {
      setCache('explore_page_0', data);
      // Don't re-render to avoid jarring UX - new data on next visit
    }
  } catch { /* background refresh failed, ignore */ }
}

async function prefetchAnimationData(ids) {
  if (!isSupabaseConfigured()) return;

  // Filter out already cached
  const uncachedIds = ids.filter(id => !animDataCache.has(id));
  if (uncachedIds.length === 0) return;

  // Fetch each one serially but in a single Promise.all — the new RPC
  // returns animation + its keyframes in one round-trip per animation.
  await Promise.all(uncachedIds.map(async (id) => {
    try {
      const rpc = await loadAnimationWithKeyframes(id);
      if (!rpc) return;
      const legacy = rpcToLegacyProject(rpc);
      animDataCache.set(id, legacy.project);
      // Seed the viewer's sessionStorage cache too so the first click on
      // a card gets an instant cache hit instead of racing another RPC.
      setCache('anim_' + id, legacy, 300000);
    } catch { /* prefetch failed, hover will just not animate */ }
  }));
}

function onHoverPlay(animId, displayEl) {
  const projectData = animDataCache.get(animId);
  if (!projectData) {
    // Data not loaded yet, show shimmer
    displayEl.classList.add('card-loading');
    return () => displayEl.classList.remove('card-loading');
  }

  // Play the first sequence
  const keyframes = projectData.keyframes || {};
  const sequences = projectData.sequences || [];
  const seq = sequences[0];
  if (!seq || !seq.steps || seq.steps.length === 0) return null;

  let step = 0;
  let timer = null;

  function playStep() {
    const s = seq.steps[step % seq.steps.length];
    const kf = keyframes[s.keyframeId];
    if (kf) {
      renderFrame(displayEl, kf);
    }
    step++;
    timer = setTimeout(playStep, s.duration);
  }

  playStep();

  return () => { clearTimeout(timer); };
}

function renderFrame(el, kf) {
  el.innerHTML = '';
  el.style.color = '';

  // .card-display is a flex container — wrap the per-cell spans in a single
  // child so they don't each become their own flex item (which would
  // destroy the grid layout).
  const frame = document.createElement('span');
  frame.style.whiteSpace = 'pre';
  for (let r = 0; r < kf.rows; r++) {
    for (let c = 0; c < kf.cols; c++) {
      const cell = kf.grid[r][c];
      const span = document.createElement('span');
      span.style.color = cell.color || kf.defaultColor || '#ca4b15';
      span.textContent = cell.char || ' ';
      frame.appendChild(span);
    }
    if (r < kf.rows - 1) frame.appendChild(document.createTextNode('\n'));
  }
  el.appendChild(frame);
}

function startEmptyMascotAnimation() {
  const mascot = document.getElementById('explore-empty-mascot');
  if (!mascot || mascot.dataset.initialized) return;
  mascot.dataset.initialized = 'true';

  const poses = {
    default:    " \u2590\u259B\u2588\u2588\u2588\u259C\u258C \n\u259D\u259C\u2588\u2588\u2588\u2588\u2588\u259B\u2598\n  \u2598\u2598 \u259D\u259D  ",
    lookLeft:   " \u2590\u259F\u2588\u2588\u2588\u259F\u258C \n\u259D\u259C\u2588\u2588\u2588\u2588\u2588\u259B\u2598\n  \u2598\u2598 \u259D\u259D  ",
    lookRight:  " \u2590\u2599\u2588\u2588\u2588\u2599\u258C \n\u259D\u259C\u2588\u2588\u2588\u2588\u2588\u259B\u2598\n  \u2598\u2598 \u259D\u259D  ",
    armsUp:     "\u2597\u259F\u259B\u2588\u2588\u2588\u259C\u2599\u2596\n \u259C\u2588\u2588\u2588\u2588\u2588\u259B \n  \u2598\u2598 \u259D\u259D  ",
  };
  const seq = [
    { pose: 'default', duration: 800 },
    { pose: 'lookLeft', duration: 500 },
    { pose: 'default', duration: 300 },
    { pose: 'lookRight', duration: 500 },
    { pose: 'default', duration: 300 },
    { pose: 'armsUp', duration: 600 },
    { pose: 'default', duration: 400 },
  ];

  let step = 0;
  function animate() {
    const { pose, duration } = seq[step % seq.length];
    mascot.textContent = poses[pose];
    step++;
    setTimeout(animate, duration);
  }
  animate();
}
